import type { TreeItem } from '@neo4j-ndl/react';
import { TreeView, Typography, Widget } from '@neo4j-ndl/react';
import type { Instance, IpFilterFilteredEntities, ProjectSummary } from '@nx/state';
import { consoleApi, useActiveOrg, useActiveProject } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { useCallback, useContext, useEffect, useState } from 'react';

import type { TreeDataProps } from './custom-entity-tree-item';
import CustomTreeItemComponent from './custom-entity-tree-item';
import { IpFilteringScopeContext } from './ip-filter-scope';
import { IP_FILTERING_SCOPE } from './types';

interface EntityTreeProps {
  onChange: (filteredEntities: IpFilterFilteredEntities) => void;
  isReadOnly?: boolean;
  filteredEntities?: IpFilterFilteredEntities;
  existingIpFilterId?: string;
}

export const EntityTree = ({
  onChange,
  isReadOnly = true,
  filteredEntities = { organizations: [], projects: [], instances: [] },
  existingIpFilterId,
}: EntityTreeProps) => {
  const { scope } = useContext(IpFilteringScopeContext);

  const organization = useActiveOrg();
  const activeProject = useActiveProject(false);

  const { data: projects = [] } = consoleApi.useListOrganizationProjectsQuery({ organizationId: organization.id });

  const { data: ipFilters = [] } = consoleApi.useListIpFiltersByOrganizationQuery(organization.id, {
    skip: !organization.capabilities.ip_filtering,
    pollingInterval: 10000,
  });

  const [getInstance] = consoleApi.useLazyGetInstanceQuery();
  const [listInstances] = consoleApi.useLazyListInstancesQuery();

  const [items, setItems] = useState<TreeItem<TreeDataProps>[]>([]);
  const [instancesWithFilters, setInstancesWithFilters] = useState<Instance[]>([]);
  const [loadingInstances, setLoadingInstances] = useState<boolean>(true);

  // Triggers to render initial Tree
  useEffect(() => {
    const getDisabledItemState = (project: ProjectSummary): { isDisabled: boolean; disabledReason: string } => {
      const projectHasFilter = isNotNullish(project.ipFilterId) && project.ipFilterId !== existingIpFilterId;
      const instanceInProjectHasFilter = instancesWithFilters.some(
        (instance) => instance.projectId === project.id && instance.ipFilterId !== existingIpFilterId,
      );
      if (projectHasFilter) {
        return {
          isDisabled: true,
          disabledReason: 'Can not apply filter to project with a filter already applied',
        };
      }
      if (instanceInProjectHasFilter) {
        return {
          isDisabled: true,
          disabledReason: 'Can not apply filter to project with instances that have a filter already applied',
        };
      }
      return {
        isDisabled: false,
        disabledReason: '',
      };
    };
    let treeItems: TreeItem<TreeDataProps>[];

    const isEntityFilteredByExistingFilter = (entityFilterID?: string) => {
      return entityFilterID === existingIpFilterId;
    };

    const fetchingItem: TreeItem<TreeDataProps> = {
      id: `fetching`,
      isSortable: false,
      canHaveSubItems: false,
      isSkeletonLoading: true,
      data: {
        id: 'fetching-id',
        text: 'Fetching instances...',
        entityType: 'instance',
      },
    };

    if (scope === IP_FILTERING_SCOPE.ORGANIZATION) {
      // Set Up Organization Tree Items
      treeItems = [
        {
          id: `org-${organization.id}`,
          isCollapsed: false,
          isSelected:
            isNotNullish(organization.ipFilterId) && isEntityFilteredByExistingFilter(organization.ipFilterId),
          isSortable: false,
          canHaveSubItems: true,
          data: {
            id: organization.id,
            text: organization.displayName,
            entityType: 'organization',
          },
          subItems: projects.map((project) => {
            const { isDisabled, disabledReason } = getDisabledItemState(project);
            return {
              id: `project-${project.id}`,
              isCollapsed: !instancesWithFilters.some(
                (instance) => instance.projectId === project.id && instance.ipFilterId === existingIpFilterId,
              ),
              isSelected: isNotNullish(project.ipFilterId) && isEntityFilteredByExistingFilter(project.ipFilterId),
              isSortable: false,
              canHaveSubItems: true,
              isSkeletonLoading: loadingInstances,
              data: {
                id: project.id,
                text: project.name,
                entityType: 'project',
                isDisabled: isDisabled,
                disabledReason: disabledReason,
              },
              subItems: [
                {
                  id: `fetching-${project.id}`,
                  isSortable: false,
                  canHaveSubItems: false,
                  isSkeletonLoading: true,
                  data: {
                    id: 'fetching-instances',
                    text: 'Fetching instances...',
                    entityType: 'instance',
                  },
                },
              ],
            };
          }),
        },
      ];
    } else {
      // Set Up Project Tree Items
      treeItems = [
        {
          id: `project-${activeProject?.id}`,
          isCollapsed: existingIpFilterId === '',
          isSelected: isNotNullish(activeProject?.ipFilterId),
          isSortable: false,
          canHaveSubItems: true,
          data: {
            id: isNotNullish(activeProject) ? activeProject.id : '',
            text: isNotNullish(activeProject) ? activeProject.name : 'No active project',
            entityType: 'project',
          },
          subItems: [fetchingItem],
        },
      ];
    }
    setItems((prev) => {
      // Only update if different
      if (JSON.stringify(prev) !== JSON.stringify(treeItems)) {
        return treeItems;
      }
      return prev;
    });
  }, [existingIpFilterId, instancesWithFilters, organization, activeProject, projects, scope, loadingInstances]);

  const updateTreeItems = (
    treeItems: TreeItem<TreeDataProps>[],
    projectId: string,
    newSubItems: TreeItem<TreeDataProps>[],
  ): TreeItem<TreeDataProps>[] => {
    return treeItems.map((item) => {
      if (item.data.entityType === 'organization' && item.canHaveSubItems) {
        return {
          ...item,
          subItems: item.subItems.map((proj) => {
            if (proj.data.id === projectId) {
              return {
                ...proj,
                subItems: newSubItems,
              };
            }
            return proj;
          }),
        };
      }
      if (item.data.id === projectId) {
        return {
          ...item,
          subItems: newSubItems,
        };
      }
      return item;
    });
  };

  const handleProjectExpand = useCallback(
    (newItems: TreeItem<TreeDataProps>[], projectId: string) => {
      if (newItems.length === 0) {
        return;
      }
      const [parentItem] = newItems;
      if (!parentItem) {
        return;
      }

      let projectItem: TreeItem<TreeDataProps> | undefined;
      if (scope === IP_FILTERING_SCOPE.ORGANIZATION) {
        const projectItems: TreeItem<TreeDataProps>[] = parentItem.canHaveSubItems ? parentItem.subItems : [];
        const projectIndex: number = projectItems.findIndex((item) => item.data.id === projectId);
        projectItem = projectItems[projectIndex];
      } else {
        projectItem = parentItem;
      }
      if (!projectItem) {
        return;
      }

      const failedFetchItem: TreeItem<TreeDataProps> = {
        id: `failed-fetch-${projectId}`,
        isSortable: false,
        isSelected: undefined,
        canHaveSubItems: false,
        data: {
          id: 'failed-fetch-instances',
          text: 'Failed to fetch instances',
          entityType: 'instance',
        },
      };

      listInstances(projectId)
        .unwrap()
        .then(async (instanceSummaries) => {
          // Establish the instances array
          let instances: TreeItem<TreeDataProps>[];

          // IF no instances, set a dummy item, ELSE fetch instance details
          if (instanceSummaries.length === 0) {
            instances = [
              {
                id: `no-instances`,
                isSelected: undefined,
                isSortable: false,
                canHaveSubItems: false,
                data: {
                  id: 'no-instances',
                  text: 'No instances found',
                  entityType: 'instance',
                },
              },
            ];
          } else {
            const instanceDetails = await Promise.all(
              instanceSummaries.map(({ id }) =>
                getInstance(id)
                  .unwrap()
                  .catch(() => {
                    return null;
                  }),
              ),
            );

            instances = instanceSummaries.map(({ id, name }, idx) => {
              const details = instanceDetails[idx];
              if (!details) {
                return failedFetchItem;
              }

              const hasCapability = details.capabilities.ip_filtering.enabled;

              // Check if filtered already (NOT by the existing filter)
              const hasFilter =
                isNotNullish(details.ipFilterId) &&
                !['ALLOW_ALL', 'DENY_ALL'].includes(details.ipFilterId) &&
                details.ipFilterId !== existingIpFilterId;

              // Check if the instance should be currently selected
              // i.e. If it is currently present in the filteredEntities state
              const instanceSelected = filteredEntities.instances ? filteredEntities.instances.includes(id) : false;

              return {
                id: `instance-${id}`,
                isSelected: instanceSelected,
                isSortable: false,
                canHaveSubItems: false,
                isSorting: false,
                data: {
                  id,
                  text: name,
                  entityType: 'instance',
                  isDisabled: !hasCapability || hasFilter,
                  disabledReason: hasCapability
                    ? 'Can not apply filter to instance with a filter already applied'
                    : details.capabilities.ip_filtering.message,
                },
              };
            });
          }
          setItems((prevState) => updateTreeItems(prevState, projectId, instances));
        })
        .catch((err) => {
          setItems((prevState) => {
            const newState = [...prevState];
            if (projectItem.canHaveSubItems) {
              projectItem.subItems = [failedFetchItem];
            }
            return newState;
          });
        });
    },
    [scope, listInstances, getInstance, filteredEntities, existingIpFilterId],
  );

  // Triggers to fetch filtered Instances
  useEffect(() => {
    async function fetchProjectIdsOfFilteredInstances() {
      const filteredInstances = ipFilters.flatMap((filter) => {
        return filter.filteredEntities?.instances ?? [];
      });
      const instances = await Promise.all(
        filteredInstances.map((instance) => {
          return getInstance(instance)
            .unwrap()
            .catch(() => {
              return null;
            });
        }),
      );
      return instances.filter((instance) => instance !== null);
    }
    async function fetchData() {
      setLoadingInstances(true);
      const newInstances = await fetchProjectIdsOfFilteredInstances();
      setInstancesWithFilters((prevInstances) => {
        // Only update state if instances actually differ
        if (JSON.stringify(prevInstances) !== JSON.stringify(newInstances)) {
          return newInstances;
        }
        return prevInstances;
      });
      setLoadingInstances(false);
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
  }, [getInstance, ipFilters]);

  // Triggers to expand projects with existing filters
  useEffect(() => {
    if (isNotNullish(existingIpFilterId)) {
      setItems((prevItems) => {
        const updatedItems = [...prevItems];
        instancesWithFilters.forEach((instance) => {
          if (instance.ipFilterId === existingIpFilterId) {
            handleProjectExpand(updatedItems, instance.projectId);
          }
        });
        return updatedItems;
      });
    }
  }, [existingIpFilterId, instancesWithFilters, handleProjectExpand]);

  const isDummyItem = (item: TreeItem<TreeDataProps>) => {
    return item.id === 'fetching' || item.id === 'no-instances' || item.id === 'failed-fetch';
  };

  const returnSelected = (itemList: TreeItem<TreeDataProps>[]) => {
    return itemList.reduce<string[]>((filtered, i) => {
      if (!isDummyItem(i) && isNotNullish(i.isSelected) && i.isSelected && !(i.data.isDisabled ?? false)) {
        filtered.push(i.data.id);
      }
      return filtered;
    }, []);
  };

  const readOnlyClasses = isReadOnly ? 'pointer-events-none opacity-50' : '';

  return (
    <Widget
      header={
        <Typography className="text-neutral-text-weak" variant="body-medium">
          Select Entities
        </Typography>
      }
    >
      <div className={`border-neutral-border-strong rounded-sm border p-2 ${readOnlyClasses}`}>
        <TreeView
          items={items}
          onItemsChanged={(newItems, reason) => {
            // First set new items to change visual state (i.e. collapse/expand)
            setItems(newItems);

            // Fetch children instances if project node is expanded
            if (reason.reason === 'expanded' && reason.item.data.entityType === 'project') {
              handleProjectExpand(newItems, reason.item.data.id);
            }

            // Update filteredEntity state based on selection in the tree
            if (reason.reason === 'selected') {
              const selectedOrgs = returnSelected(newItems);
              const projs = newItems.flatMap((o) => (o.canHaveSubItems ? o.subItems : []));
              const selectedProjects = returnSelected(projs);
              const selectedInstances = returnSelected(projs.flatMap((p) => (p.canHaveSubItems ? p.subItems : [])));

              onChange({
                organizations: selectedOrgs,
                projects: selectedProjects,
                instances: selectedInstances,
              });
            }
          }}
          TreeItemComponent={CustomTreeItemComponent}
        />
      </div>
    </Widget>
  );
};
